import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";

// Register necessary components
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, Filler);

const DynamicResponseView = ({ response }) => {
    const [columns, setColumns] = useState([]);
    const [selectedColumns, setSelectedColumns] = useState({ x: "", y: [] });
    const [graphData, setGraphData] = useState(null);
    const [graphType, setGraphType] = useState("Bar");
    const [selectedTab, setSelectedTab] = useState("table");

    useEffect(() => {
        if (response && Array.isArray(response.responsePacket) && response.responsePacket.length) {
            const keys = Object.keys(response.responsePacket[0]);
            setColumns(keys);
        }
    }, [response]);

    const formatValue = (value) => {
        if (value && typeof value === "object" && value.$date) {
            return new Date(value.$date).toLocaleString();
        }
        if (value && typeof value === "object") {
            return JSON.stringify(value, null, 2);
        }
        return value;
    };

    const generateGraphData = () => {
        if (!selectedColumns.x || !selectedColumns.y.length) {
            alert("Please select both X and Y columns.");
            return;
        }

        const getRandomColor = () => {
            const letters = "0123456789ABCDEF";
            let color = "#";
            for (let i = 0; i < 6; i++) {
                color += letters[Math.floor(Math.random() * 16)];
            }
            return color;
        };

        const xData = response.responsePacket.map((item) => formatValue(item[selectedColumns.x]));
        const datasets = selectedColumns.y.map((col) => {
            const color = getRandomColor(); // Generate a unique color for this dataset
            return {
                label: col,
                data: response.responsePacket.map((item) => parseFloat(item[col]) || 0),
                borderColor: color,
                backgroundColor: `${color}80`, // Semi-transparent background
                fill: graphType === "Area",
                tension: 0.4,
            };
        });

        setGraphData({
            labels: xData,
            datasets,
        });
    };

    const renderTabs = () => (
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "1rem" }}>
            <button
                onClick={() => setSelectedTab("table")}
                style={{
                    padding: "10px 20px",
                    margin: "0 5px",
                    backgroundColor: selectedTab === "table" ? "#007bff" : "#ddd",
                    color: "white",
                    border: "none",
                    borderRadius: "5px",
                }}
            >
                Table
            </button>
            <button
                onClick={() => setSelectedTab("graph")}
                style={{
                    padding: "10px 20px",
                    margin: "0 5px",
                    backgroundColor: selectedTab === "graph" ? "#007bff" : "#ddd",
                    color: "white",
                    border: "none",
                    borderRadius: "5px",
                }}
            >
                Graph
            </button>
        </div>
    );

    const renderGraphSelection = () => (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "20px",
                marginBottom: "20px",
                padding: "10px",
                backgroundColor: "#f9f9f9",
                borderRadius: "8px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
        >
            <div>
                <label style={{ fontWeight: "bold" }}>X-Axis:</label>
                <select
                    onChange={(e) => setSelectedColumns({ ...selectedColumns, x: e.target.value })}
                    value={selectedColumns.x}
                    style={{
                        marginLeft: "10px",
                        padding: "8px",
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                    }}
                >
                    <option value="">Select Column</option>
                    {columns.map((col) => (
                        <option key={col} value={col}>
                            {col}
                        </option>
                    ))}
                </select>
            </div>
            <div>
                <label style={{ fontWeight: "bold" }}>Y-Axis:</label>
                <select
                    multiple
                    onChange={(e) =>
                        setSelectedColumns({
                            ...selectedColumns,
                            y: Array.from(e.target.selectedOptions, (option) => option.value),
                        })
                    }
                    value={selectedColumns.y}
                    style={{
                        marginLeft: "10px",
                        padding: "8px",
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                        minWidth: "150px",
                    }}
                >
                    {columns.map((col) => (
                        <option key={col} value={col}>
                            {col}
                        </option>
                    ))}
                </select>
            </div>
            <div>
                <label style={{ fontWeight: "bold" }}>Graph Type:</label>
                <select
                    onChange={(e) => setGraphType(e.target.value)}
                    value={graphType}
                    style={{
                        marginLeft: "10px",
                        padding: "8px",
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                    }}
                >
                    <option value="Bar">Bar</option>
                    <option value="Line">Line</option>
                    <option value="Area">Area</option>
                </select>
            </div>
            <button
                onClick={generateGraphData}
                style={{
                    padding: "10px 20px",
                    backgroundColor: "#007bff",
                    color: "white",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                }}
            >
                Generate Graph
            </button>
        </div>
    );

    const renderGraph = () => {
        if (!graphData) return null;

        const options = {
            responsive: true,
            plugins: {
                legend: { display: true },
                tooltip: { enabled: true },
                title: {
                    display: true,
                    text: "Custom Graph",
                },
            },
            scales: {
                x: { title: { display: true, text: "X-Axis" } },
                y: { title: { display: true, text: "Y-Axis" } },
            },
        };

        return (
            <div style={{ marginTop: "2rem" }}>
                {graphType === "Bar" && <Bar data={graphData} options={options} />}
                {graphType === "Line" && <Line data={graphData} options={options} />}
                {graphType === "Area" && <Line data={graphData} options={options} />}
            </div>
        );
    };

    return (
        <div>
            {renderTabs()}
            {selectedTab === "table" && (
                <div style={{ overflowX: "auto", maxHeight: "400px" }}>
                    <DataTable
                        title="Table View"
                        columns={columns.map((col) => ({
                            name: col,
                            selector: (row) => formatValue(row[col]),
                        }))}
                        data={response?.responsePacket || []}
                        pagination
                    />
                </div>
            )}
            {selectedTab === "graph" && (
                <>
                    {renderGraphSelection()}
                    {renderGraph()}
                </>
            )}
        </div>
    );
};

export default DynamicResponseView;
